import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles removing selected items from the cart.
   */
  onClearSelected() {
    const selectedCheckboxes = document.querySelectorAll('.cart-item-checkbox:checked');

    if (selectedCheckboxes.length === 0) return;

    this.#disableCartItems();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // updates 객체 생성 (수량 0 = 삭제)
    /** @type {Record<string, number>} */
    const updates = {};
    selectedCheckboxes.forEach((checkbox) => {
      if (checkbox instanceof HTMLInputElement) {
        const key = checkbox.dataset.itemKey;
        if (key) updates[key] = 0;
      }
    });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then((parsedResponse) => {
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: parsedResponse.item_count,
            source: 'cart-items-component',
            sections: parsedResponse.sections,
          })
        );

        const sectionHTML = parsedResponse.sections?.[this.sectionId];

        if (sectionHTML) {
          morphSection(this.sectionId, sectionHTML);
          this.#syncCartBottomSections(this.#getCartItemCountFromSectionHTML(sectionHTML));
          document.dispatchEvent(new CustomEvent('cart:morph-complete'));
        } else {
          window.location.reload();
        }
      })
      .catch((error) => {
        console.error('Failed to remove selected items:', error);
        window.location.reload();
      })
      .finally(() => {
        this.#enableCartItems();
      });
  }

  /**
   * Handles clearing all items from the cart.
   */
  onClearAll() {
    this.#disableCartItems();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_url}/clear.js`, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then((parsedResponse) => {
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: 0,
            source: 'cart-items-component',
            sections: parsedResponse.sections,
          })
        );

        const sectionHTML = parsedResponse.sections?.[this.sectionId];

        // Reload the section to show empty cart state
        if (sectionHTML) {
          morphSection(this.sectionId, sectionHTML);
          this.#syncCartBottomSections(this.#getCartItemCountFromSectionHTML(sectionHTML));
          document.dispatchEvent(new CustomEvent('cart:morph-complete'));
        } else {
          window.location.reload();
        }
      })
      .catch((error) => {
        console.error('Failed to clear cart:', error);
        window.location.reload();
      })
      .finally(() => {
        this.#enableCartItems();
      });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        const sectionHTML = parsedResponseText.sections?.[this.sectionId];

        if (sectionHTML) {
          const newCartItemCount = this.#getCartItemCountFromSectionHTML(sectionHTML);

          this.#updateQuantitySelectors(parsedResponseText);

          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: newCartItemCount,
              source: 'cart-items-component',
              sections: parsedResponseText.sections,
            })
          );

          await morphSection(this.sectionId, sectionHTML);

          this.#syncCartBottomSections(newCartItemCount);
          this.#updateCartQuantitySelectorButtonStates();
          document.dispatchEvent(new CustomEvent('cart:morph-complete'));
        } else if (parsedResponseText.errors) {
          await sectionRenderer.renderSection(this.sectionId, { cache: false });
        }

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#syncCartBottomSections(this.#getCartItemCountFromSectionHTML(cartItemsHtml));

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      document.dispatchEvent(new CustomEvent('cart:morph-complete'));
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then((sectionHTML) => {
        this.#syncCartBottomSections(this.#getCartItemCountFromSectionHTML(sectionHTML));
      });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Extracts the cart item count from rendered cart section HTML.
   * @param {string} sectionHTML - The rendered cart section HTML.
   * @returns {number} The cart item count.
   */
  #getCartItemCountFromSectionHTML(sectionHTML) {
    const sectionDocument = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const cartHiddenItemCount = sectionDocument.querySelector('[ref="cartItemCount"]')?.textContent;

    return cartHiddenItemCount ? parseInt(cartHiddenItemCount, 10) : 0;
  }

  /**
   * Syncs the cart page bottom sections with the current cart count.
   * @param {number} itemCount - The current cart item count.
   */
  #syncCartBottomSections(itemCount) {
    if (window.Shopify?.designMode) return;

    const normalizedItemCount = Number(itemCount) || 0;
    const shouldShowRecommended = normalizedItemCount === 0;

    document.querySelectorAll('[data-cart-bottom-section]').forEach((section) => {
      if (!(section instanceof HTMLElement)) return;

      const sectionType = section.dataset.cartBottomSection;
      if (sectionType !== 'recommended' && sectionType !== 'recently-viewed') return;

      const shouldHide = sectionType === 'recommended' ? !shouldShowRecommended : shouldShowRecommended;
      section.toggleAttribute('hidden', shouldHide);

      if (shouldHide) {
        section.setAttribute('aria-hidden', 'true');
      } else {
        section.removeAttribute('aria-hidden');
      }
    });
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
